import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth, Show } from "@clerk/react";
import { Plus, Star, Menu } from "lucide-react";
import { useUserStore } from "@/stores/user-store";
import { CouchLogo } from "@/components/ui/couch-logo";
import { AuthModal } from "@/components/ui/auth-modal";

interface TopBarProps {
  onMenuClick: () => void;
}

export function TopBar({ onMenuClick }: TopBarProps) {
  return (
    <header className="flex items-center justify-between px-4 md:px-6 h-14 border-b border-[rgba(45,49,39,0.06)] bg-[#faf6ed]/70 backdrop-blur-xl">
      <div className="flex items-center gap-3">
        <button
          onClick={onMenuClick}
          className="md:hidden p-2 -ml-2 rounded-xl text-muted-foreground hover:text-foreground hover:bg-[rgba(45,49,39,0.04)] transition-colors"
          aria-label="Open menu"
        >
          <Menu className="size-5" />
        </button>
        <Link
          to="/"
          className="flex items-center gap-2.5 hover:opacity-90 transition-opacity"
        >
          <CouchLogo className="size-7 md:size-8" />
          <span className="text-[15px] font-extrabold tracking-tight text-foreground">
            CouchStack
          </span>
        </Link>
      </div>
      <Actions />
    </header>
  );
}

function Actions() {
  const { isLoaded: isClerkLoaded } = useAuth();
  const { credits, isLoading: isUserLoading } = useUserStore();
  const [showAuth, setShowAuth] = useState(false);

  if (!isClerkLoaded) {
    return <div className="h-8 w-32 rounded-full bg-[rgba(45,49,39,0.04)] animate-pulse" />;
  }

  return (
    <div className="flex items-center gap-2.5">
      <Show when="signed-out">
        <button
          onClick={() => setShowAuth(true)}
          className="btn-primary text-[13px] py-1.5 px-4"
        >
          Get Started
        </button>
        <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} />
      </Show>
      <Show when="signed-in">
        <CreditsCounter credits={credits} isLoading={isUserLoading} />
        <Link to="/create/text-to-image">
          <button className="btn-primary flex items-center gap-1.5 text-[13px] py-1.5 px-3.5">
            <Plus className="size-3.5" strokeWidth={2.5} />
            <span className="hidden md:inline">Create</span>
          </button>
        </Link>
      </Show>
    </div>
  );
}

function CreditsCounter({
  credits,
  isLoading,
}: {
  credits: number;
  isLoading: boolean;
}) {
  if (isLoading) {
    return <div className="h-8 w-20 rounded-full bg-[rgba(45,49,39,0.04)] animate-pulse" />;
  }

  return (
    <div className="flex items-center rounded-full bg-amber-50/80 border border-amber-200/50 text-[13px] transition-all duration-200 hover:border-amber-200/70 hover:shadow-sm">
      <Link
        to="/billing?src=topbar_balance"
        className="flex items-center gap-1.5 pl-3 pr-2 py-1.5 group"
      >
        <Star className="size-3.5 text-amber-600 fill-amber-600/30 group-hover:fill-amber-600/50 transition-colors" />
        <span className="font-bold text-amber-800 tabular-nums">
          {credits.toLocaleString()}
        </span>
      </Link>
      <div className="w-px h-4 bg-amber-200/70" />
      <Link
        to="/billing?src=topbar_buy"
        className="pl-2 pr-3 py-1.5 font-bold text-amber-700 hover:text-amber-900 transition-colors"
      >
        Buy
      </Link>
    </div>
  );
}
